import { useEffect, useRef, useState } from 'react'
import { apiClient } from '@/lib/api/client'
import { readSyncStatus, writeSyncStatus } from '@/src/lib/onboarding/progress'
import { changesFrom, fromServer, mergeProgress } from '@/src/lib/onboarding/sync'
import type { TourProgress } from '@/src/lib/onboarding/tour-machine'

/**
 * Keeps a person's tour progress on the server, so it follows them to every
 * device. On load it reads the server's copy and merges in this browser's;
 * the first time, and after a change that never reached the server, all of
 * this browser's copy goes up (the server merges, so nothing is lost twice).
 * Changes go up one at a time, in order. Offline, the tours run from this
 * browser's copy, and it tries again once the browser is back online.
 *
 * `ready` is false until the server has answered or failed to: Getting
 * started waits for it, so a new device doesn't offer it again.
 */
export function useProgressSync(
  userId: string,
  progress: TourProgress,
  setProgress: (update: (current: TourProgress) => TourProgress) => void,
) {
  const [ready, setReady] = useState(false)
  const [attempt, setAttempt] = useState(0)
  /** What the server holds, as far as this page knows; null until it has said. */
  const saved = useRef<TourProgress | null>(null)
  /** Tours changed on this page before the server answered: their step is ours. */
  const changedHere = useRef(new Set<string>())
  const before = useRef(progress)
  const queue = useRef<Promise<unknown>>(Promise.resolve())

  useEffect(() => {
    let cancelled = false
    apiClient
      .getOnboardingProgress()
      .then((payload) => {
        if (cancelled) return
        const server = fromServer(payload)
        const status = readSyncStatus(userId)
        const ours = !status.imported || status.unsaved ? 'all' : changedHere.current
        saved.current = server
        setProgress((current) => mergeProgress(server, current, ours))
        setReady(true)
      })
      .catch(() => {
        // Offline, or the server is down: carry on from this browser's copy.
        if (!cancelled) setReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [userId, setProgress, attempt])

  useEffect(() => {
    const retry = () => {
      if (!saved.current || readSyncStatus(userId).unsaved) setAttempt((count) => count + 1)
    }
    window.addEventListener('online', retry)
    return () => window.removeEventListener('online', retry)
  }, [userId])

  useEffect(() => {
    if (!ready) {
      for (const id of Object.keys(changesFrom(before.current, progress)?.tours ?? {})) {
        changedHere.current.add(id)
      }
      return
    }
    const update = changesFrom(saved.current, progress)
    if (!update) {
      if (saved.current && !readSyncStatus(userId).imported) {
        writeSyncStatus(userId, { imported: true, unsaved: false })
      }
      return
    }
    saved.current = progress
    queue.current = queue.current
      .then(() => apiClient.saveOnboardingProgress(update))
      .then(() => writeSyncStatus(userId, { imported: true, unsaved: false }))
      .catch(() =>
        writeSyncStatus(userId, { imported: readSyncStatus(userId).imported, unsaved: true }),
      )
  }, [progress, ready, userId])

  return { ready }
}
